import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { EdgeRectangleShape } from '@sorare/core/src/atoms/ui/EdgeRectangleShape';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

import type { PromoButton_conversionCredit } from './__generated__/index.graphql';

const Root = styled.div`
  --notch-height: calc(5 * var(--unit));
  position: relative;
  padding-top: var(--notch-height);
  &.fullWidth {
    width: 100%;
  }
`;
const NotchWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`;
const StyledHorizontal = styled(Horizontal)`
  height: var(--notch-height);
  text-wrap: nowrap;
`;
const StyledImg = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;
const StyledButton = styled(LoadingButton)`
  &.medium,
  &.large {
    min-width: calc(var(--min-width) + 14 * var(--unit));
  }
`;

interface Props extends Omit<ButtonProps, 'loading'> {
  conversionCredit: Nullable<PromoButton_conversionCredit>;
  loading?: boolean;
  customMessage?: ReactNode;
}

export const PromoButton = ({
  conversionCredit,
  children,
  loading,
  customMessage,
  ...buttonProps
}: Props) => {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { toMonetaryAmount } = useMonetaryAmount();

  const { maxDiscount } = conversionCredit || {};
  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(maxDiscount),
    hideFractionDigits: true,
  });

  const hasDiscount = !!maxDiscount;
  const fullWidth = !isTabletAndAbove;

  if (!hasDiscount) {
    return (
      <LoadingButton {...buttonProps} loading={!!loading} fullWidth={fullWidth}>
        {children}
      </LoadingButton>
    );
  }

  return (
    <Root className={classNames({ fullWidth })}>
      <NotchWrapper>
        <EdgeRectangleShape
          shape="trapezoid"
          padding="0 var(--quadruple-unit)"
          offset="var(--triple-unit)"
          background="#111F1C"
          border="linear-gradient(to bottom, transparent 0%, #111F1C 70%), linear-gradient(to right, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8)"
        >
          <StyledHorizontal ref={ref}>
            <StyledImg
              src={`${FRONTEND_ASSET_HOST}/rewards/credit.png`}
              alt=""
            />
            <BrandS>
              {customMessage || (
                <FormattedMessage
                  id="PromoButton.discount"
                  defaultMessage="{value} OFF*"
                  values={{ value: main }}
                />
              )}
            </BrandS>
          </StyledHorizontal>
        </EdgeRectangleShape>
      </NotchWrapper>
      <StyledButton
        {...buttonProps}
        loading={!!loading}
        fullWidth={!isTabletAndAbove}
        style={{ '--min-width': `${width}px` } as CSSProperties}
      >
        {children}
      </StyledButton>
    </Root>
  );
};

PromoButton.fragments = {
  conversionCredit: gql`
    fragment PromoButton_conversionCredit on ConversionCredit {
      id
      maxDiscount {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<PromoButton_conversionCredit>,
};
